import { useMutation } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  CreateRedeemCodeMutation,
  CreateRedeemCodeMutationVariables,
  DeleteRedeemCodeMutation,
  DeleteRedeemCodeMutationVariables,
  UpdateRedeemCodeMutation,
  UpdateRedeemCodeMutationVariables,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { CREATE_REDEEM_CODE, DELETE_REDEEM_CODE, UPDATE_REDEEM_CODE } from '../graphql';
import {
  APP_VERSION_OPERATORS,
  EditableReward,
  isValidRedeemCode,
  parseFilterUserIds,
  RedeemCodeItem,
  rewardUnionToInput,
} from '../utils';
import RedeemCodeDateField from './RedeemCodeDateField';
import RedeemCodeRewardsEditor from './RedeemCodeRewardsEditor';

type Props = {
  // null = create mode, otherwise the redeem code being edited
  redeemCode: RedeemCodeItem | null;
  defaultEnabledIos?: boolean;
  defaultEnabledAndroid?: boolean;
  onSaved: (redeemCode: RedeemCodeItem) => void;
  onCancel?: () => void;
  onDeleted?: (id: string) => void;
};

const defaultStartDate = () => moment().utc().set({ second: 0, millisecond: 0 }).toDate();
const defaultEndDate = () => moment().utc().set({ second: 0, millisecond: 0 }).add(7, 'days').toDate();

// First client version shipping the redeem code feature — shown as a hint, not prefilled
const MIN_SUPPORTED_APP_VERSION = '003.036.000';

const RedeemCodeForm = ({
  redeemCode,
  defaultEnabledIos = false,
  defaultEnabledAndroid = false,
  onSaved,
  onCancel,
  onDeleted,
}: Props) => {
  const isCreateMode = redeemCode === null;

  const [code, setCode] = useState(redeemCode?.code ?? '');
  const [enabledIos, setEnabledIos] = useState(redeemCode?.enabledIos ?? defaultEnabledIos);
  const [enabledAndroid, setEnabledAndroid] = useState(redeemCode?.enabledAndroid ?? defaultEnabledAndroid);
  const [successMessage, setSuccessMessage] = useState(redeemCode?.successMessage ?? '');
  const [startDate, setStartDate] = useState<Date>(
    redeemCode ? new Date(redeemCode.startDate) : defaultStartDate()
  );
  const [noExpiry, setNoExpiry] = useState(redeemCode ? _.isNil(redeemCode.endDate) : false);
  const [endDate, setEndDate] = useState<Date>(
    redeemCode?.endDate ? new Date(redeemCode.endDate) : defaultEndDate()
  );
  const [filterUserIdsText, setFilterUserIdsText] = useState((redeemCode?.filterUserIds ?? []).join('\n'));
  const [filterAppVersion, setFilterAppVersion] = useState(redeemCode?.filterAppVersion ?? '');
  const [filterAppVersionOperator, setFilterAppVersionOperator] = useState<string>(
    redeemCode?.filterAppVersionOperator ?? '>='
  );
  const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<string>(
    redeemCode?.filterSubscriptionTier ?? ''
  );
  const [filterUserLevelText, setFilterUserLevelText] = useState<string>(
    redeemCode?.filterUserLevel ? String(redeemCode.filterUserLevel) : ''
  );
  const [filterUserMinCreationDate, setFilterUserMinCreationDate] = useState<Date | null>(
    redeemCode?.filterUserMinCreationDate ? new Date(redeemCode.filterUserMinCreationDate) : null
  );
  const [filterUserMaxCreationDate, setFilterUserMaxCreationDate] = useState<Date | null>(
    redeemCode?.filterUserMaxCreationDate ? new Date(redeemCode.filterUserMaxCreationDate) : null
  );
  const [rewards, setRewards] = useState<EditableReward[]>([]);

  const [createRedeemCode, { loading: createLoading }] = useMutation<
    CreateRedeemCodeMutation,
    CreateRedeemCodeMutationVariables
  >(CREATE_REDEEM_CODE);
  const [updateRedeemCode, { loading: updateLoading }] = useMutation<
    UpdateRedeemCodeMutation,
    UpdateRedeemCodeMutationVariables
  >(UPDATE_REDEEM_CODE);
  const [deleteRedeemCode, { loading: deleteLoading }] = useMutation<
    DeleteRedeemCodeMutation,
    DeleteRedeemCodeMutationVariables
  >(DELETE_REDEEM_CODE);

  const loading = createLoading || updateLoading || deleteLoading;

  // Reset the form whenever another redeem code is selected or create mode is entered
  useEffect(() => {
    setCode(redeemCode?.code ?? '');
    setEnabledIos(redeemCode?.enabledIos ?? defaultEnabledIos);
    setEnabledAndroid(redeemCode?.enabledAndroid ?? defaultEnabledAndroid);
    setSuccessMessage(redeemCode?.successMessage ?? '');
    setStartDate(redeemCode ? new Date(redeemCode.startDate) : defaultStartDate());
    setNoExpiry(redeemCode ? _.isNil(redeemCode.endDate) : false);
    setEndDate(redeemCode?.endDate ? new Date(redeemCode.endDate) : defaultEndDate());
    setFilterUserIdsText((redeemCode?.filterUserIds ?? []).join('\n'));
    setFilterAppVersion(redeemCode?.filterAppVersion ?? '');
    setFilterAppVersionOperator(redeemCode?.filterAppVersionOperator ?? '>=');
    setFilterSubscriptionTier(redeemCode?.filterSubscriptionTier ?? '');
    setFilterUserLevelText(redeemCode?.filterUserLevel ? String(redeemCode.filterUserLevel) : '');
    setFilterUserMinCreationDate(
      redeemCode?.filterUserMinCreationDate ? new Date(redeemCode.filterUserMinCreationDate) : null
    );
    setFilterUserMaxCreationDate(
      redeemCode?.filterUserMaxCreationDate ? new Date(redeemCode.filterUserMaxCreationDate) : null
    );
    setRewards(
      (redeemCode?.rewards ?? [])
        .map(rewardUnionToInput)
        .filter((reward): reward is EditableReward => !_.isNil(reward))
    );
  }, [defaultEnabledAndroid, defaultEnabledIos, redeemCode]);

  const trimmedCode = code.trim();
  const codeIsValid = isValidRedeemCode(trimmedCode);
  const datesAreValid = noExpiry || startDate.getTime() < endDate.getTime();
  const filterUserLevel = filterUserLevelText.trim() ? Number(filterUserLevelText) : null;
  const userLevelIsValid = filterUserLevel === null || (Number.isInteger(filterUserLevel) && filterUserLevel >= 1);
  const creationDatesAreValid =
    !filterUserMinCreationDate ||
    !filterUserMaxCreationDate ||
    filterUserMinCreationDate.getTime() <= filterUserMaxCreationDate.getTime();
  const canSave =
    codeIsValid && datesAreValid && userLevelIsValid && creationDatesAreValid && rewards.length > 0 && !loading;

  const handleSave = () => {
    const commonVariables = {
      code: trimmedCode,
      enabledIos,
      enabledAndroid,
      successMessage: successMessage.trim() ? successMessage.trim() : null,
      startDate,
      endDate: noExpiry ? null : endDate,
      filterAppVersion: filterAppVersion.trim() ? filterAppVersion.trim() : null,
      filterAppVersionOperator: filterAppVersion.trim() ? filterAppVersionOperator : null,
      filterSubscriptionTier: filterSubscriptionTier ? (filterSubscriptionTier as UserSubscriptionTier) : null,
      filterUserLevel,
      filterUserMinCreationDate,
      filterUserMaxCreationDate,
      rewards: rewards.map(({ type, amount, itemId }) => ({ type, amount, itemId })),
    };

    if (isCreateMode) {
      createRedeemCode({
        variables: { ...commonVariables, filterUserIds: parseFilterUserIds(filterUserIdsText) },
      })
        .then(({ data }) => {
          if (data?.createRedeemCode) {
            onSaved(data.createRedeemCode as RedeemCodeItem);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      updateRedeemCode({
        variables: {
          ...commonVariables,
          id: redeemCode.id,
          filterUserIds: parseFilterUserIds(filterUserIdsText) ?? [],
        },
      })
        .then(({ data }) => {
          if (data?.updateRedeemCode) {
            onSaved(data.updateRedeemCode as RedeemCodeItem);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleDelete = () => {
    if (isCreateMode || !onDeleted) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete redeem code "${redeemCode.code}"? This cannot be undone.`)) {
      return;
    }
    deleteRedeemCode({ variables: { id: redeemCode.id } })
      .then(() => {
        onDeleted(redeemCode.id);
      })
      .catch((error) => {
        console.error(error);
        // eslint-disable-next-line no-alert
        alert(
          error?.message?.includes('REDEEM_CODE_HAS_REDEMPTIONS')
            ? 'This code cannot be deleted because players have already redeemed it. Turn off both platform switches to disable it instead.'
            : `Failed to delete the redeem code: ${error?.message ?? 'unknown error'}`
        );
      });
  };

  return (
    <>
      <div
        className="rounded p-2 mb-3"
        style={
          enabledIos || enabledAndroid
            ? { backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }
            : { backgroundColor: '#f8d7da', border: '2px solid #dc3545' }
        }
      >
        <div className="d-flex align-items-center" style={{ gap: '1.5rem' }}>
          <span className="font-weight-bold">Available on</span>
          <Form.Check
            type="switch"
            id="redeem-code-enabled-ios"
            label={<span className="font-weight-bold">iOS</span>}
            checked={enabledIos}
            onChange={(e) => setEnabledIos(e.target.checked)}
          />
          <Form.Check
            type="switch"
            id="redeem-code-enabled-android"
            label={<span className="font-weight-bold">Android</span>}
            checked={enabledAndroid}
            onChange={(e) => setEnabledAndroid(e.target.checked)}
          />
        </div>
        {!enabledIos && !enabledAndroid && (
          <Form.Text className="text-danger mb-0">
            Disabled on both platforms: every redemption attempt fails with &quot;Invalid code&quot; until the code is
            enabled again. The campaign and its redemption history are kept.
          </Form.Text>
        )}
        {enabledIos !== enabledAndroid && (
          <Form.Text className="text-muted mb-0">
            Players on {enabledIos ? 'Android' : 'iOS'} are rejected with &quot;Invalid code&quot;. This also acts as
            the platform targeting of the campaign.
          </Form.Text>
        )}
      </div>

      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. WELCOME2026"
              value={code}
              isInvalid={code.length > 0 && !codeIsValid}
              onChange={(e) => setCode(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              Only uppercase letters A-Z and digits 0-9 are allowed (no spaces, lowercase or special characters).
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Success message (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Shown in the 'Redeemed Successfully!' popup. Leave empty to use the default message."
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
            />
          </Form.Group>

          <RedeemCodeDateField label="Start date" value={startDate} onChange={setStartDate} />
          <div
            className="rounded p-2 mb-3"
            style={
              noExpiry
                ? { backgroundColor: '#ffc107', border: '2px solid #d39e00' }
                : { backgroundColor: '#fff8e1', border: '2px solid #ffc107' }
            }
          >
            <Form.Check
              type="checkbox"
              id="redeem-code-no-expiry"
              label={
                <span className="font-weight-bold">
                  <i className={`fas fa-infinity mr-2 ${noExpiry ? 'text-dark' : 'text-warning'}`} />
                  No expiry (lifetime code)
                </span>
              }
              checked={noExpiry}
              onChange={(e) => setNoExpiry(e.target.checked)}
            />
            {noExpiry && (
              <Form.Text className="text-dark mb-0">
                This code never expires. Use the platform switches above to disable it at any time.
              </Form.Text>
            )}
          </div>
          {!noExpiry && <RedeemCodeDateField label="Expiry date" value={endDate} onChange={setEndDate} />}
          {!datesAreValid && <Form.Text className="text-danger">Start date must be before expiry date.</Form.Text>}
        </Col>

        <Col md={6}>
          <fieldset className="border rounded p-3 mb-3">
            <legend className="w-auto px-2 h6 mb-0">Matcher (leave empty for all players)</legend>

            <Form.Group>
              <Form.Label>User IDs</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Comma or newline separated user IDs."
                value={filterUserIdsText}
                onChange={(e) => setFilterUserIdsText(e.target.value)}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>App version</Form.Label>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <Form.Control
                  as="select"
                  style={{ maxWidth: 90 }}
                  value={filterAppVersionOperator}
                  disabled={!filterAppVersion.trim()}
                  onChange={(e) => setFilterAppVersionOperator(e.target.value)}
                >
                  {APP_VERSION_OPERATORS.map((operator) => (
                    <option key={operator} value={operator}>
                      {operator}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control
                  type="text"
                  placeholder="Leave empty for all versions"
                  value={filterAppVersion}
                  onChange={(e) => setFilterAppVersion(e.target.value)}
                />
              </div>
              <Form.Text className="text-muted">
                Leave empty to allow all versions; the operator compares the client version against this value
                (default {'>='} = minimum version). Note: the first client version supporting redeem codes is{' '}
                {MIN_SUPPORTED_APP_VERSION} — older clients have no redeem code menu.
              </Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Minimum player level</Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="Leave empty for all levels"
                value={filterUserLevelText}
                isInvalid={!userLevelIsValid}
                onChange={(e) => setFilterUserLevelText(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                The minimum level must be a whole number of at least 1.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Minimum subscription tier</Form.Label>
              <Form.Control
                as="select"
                value={filterSubscriptionTier}
                onChange={(e) => setFilterSubscriptionTier(e.target.value)}
              >
                <option value="">Any (no subscription required)</option>
                {Object.values(UserSubscriptionTier).map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Account creation date</Form.Label>
              <Form.Check
                type="checkbox"
                id="redeem-code-filter-min-creation"
                label="Only accounts created on/after…"
                checked={filterUserMinCreationDate !== null}
                onChange={(e) => setFilterUserMinCreationDate(e.target.checked ? defaultStartDate() : null)}
              />
              {filterUserMinCreationDate && (
                <RedeemCodeDateField
                  label="Created on/after"
                  value={filterUserMinCreationDate}
                  onChange={setFilterUserMinCreationDate}
                />
              )}
              <Form.Check
                type="checkbox"
                id="redeem-code-filter-max-creation"
                label="Only accounts created on/before…"
                checked={filterUserMaxCreationDate !== null}
                onChange={(e) => setFilterUserMaxCreationDate(e.target.checked ? defaultStartDate() : null)}
              />
              {filterUserMaxCreationDate && (
                <RedeemCodeDateField
                  label="Created on/before"
                  value={filterUserMaxCreationDate}
                  onChange={setFilterUserMaxCreationDate}
                />
              )}
              {!creationDatesAreValid && (
                <Form.Text className="text-danger">
                  The &quot;created on/after&quot; date must be before the &quot;created on/before&quot; date.
                </Form.Text>
              )}
            </Form.Group>
          </fieldset>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={{ span: 8, offset: 2 }}>
          <RedeemCodeRewardsEditor rewards={rewards} onChange={setRewards} centered />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col className="d-flex align-items-center justify-content-end">
          {!isCreateMode && onDeleted && (
            <Button variant="outline-danger" className="mr-auto" disabled={loading} onClick={handleDelete}>
              <i className="fas fa-trash-alt mr-2" />
              Delete
            </Button>
          )}
          {!isCreateMode && redeemCode.endDate && moment(redeemCode.endDate).isBefore(moment()) && (
            <span className="text-muted mr-3">This code has expired. Players can no longer redeem it.</span>
          )}
          {isCreateMode && onCancel && (
            <Button variant="outline-secondary" className="mr-2" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant={isCreateMode ? 'success' : 'primary'} disabled={!canSave} onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : isCreateMode ? 'Create' : 'Save'}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default RedeemCodeForm;
